import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Edit, Trash2, Eye, FileText } from "lucide-react";
import {
  AdminLayout,
  PageHeader,
  Card,
  Button,
  Badge,
  Grid,
  PaginationControls,
  LoadingSpinner,
  ErrorDisplay,
  EmptyState,
  useToast,
} from "../components/AdminSharedComponents";
import api from "../../../../utils/api";

const Research = () => {
  // State for research projects
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const toast = useToast();

  // Fetch research data from API
  const fetchResearch = async (area = "", status = "", keyword = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (area && area !== "all") params.append("area", area);
      if (status) params.append("status", status);
      if (keyword) params.append("keyword", keyword);

      const response = await api.get(`/api/research?${params.toString()}`);
      setProjects(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching research data:", err);
      setError("Gagal memuat data penelitian. Silakan coba lagi nanti.");
      toast.error("Gagal memuat data penelitian");
    } finally {
      setLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchResearch();
  }, []);

  // Handle search and filter changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const area = filter !== "all" ? filter : "";
      fetchResearch(area, "", searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filter, searchTerm]);

  // Filter research projects - this is now handled by the backend, but we keep some client-side filtering for UI responsiveness
  const filteredProjects = projects;

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProjects.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  // Pagination controls
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  // Handle delete research project
  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this research project?")
    ) {
      try {
        await api.delete(`/api/research/${id}`);
        // Refresh the data after deletion
        fetchResearch(filter !== "all" ? filter : "", "", searchTerm);
      } catch (err) {
        console.error("Error deleting research:", err);
        alert("Failed to delete research. Please try again.");
      }
    }
  };

  // Map area IDs to display names
  const areaNames = {
    minerals: "Mineral Research",
    cathodic: "Cathodic Protection",
    corrosion: "Corrosion Science",
  };

  // Status badge variants
  const getStatusVariant = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "default";
      case "archived":
        return "danger";
      default:
        return "default";
    }
  };

  // Area badge variants
  const getAreaVariant = (area) => {
    switch (area) {
      case "minerals":
        return "success";
      case "cathodic":
        return "primary";
      case "corrosion":
        return "warning";
      default:
        return "default";
    }
  };

  // Get area counts for the overview cards
  const getActiveProjectCount = (area) => {
    return projects.filter((p) => p.area === area && p.status === "active")
      .length;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD format
  };

  // Mobile card view for research items
  const renderMobileCard = (project) => (
    <Card key={project.id} className="mb-4">
      <h4 className="font-medium text-base mb-2 text-primary-dark">
        {project.title}
      </h4>
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
        {project.description}
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        <Badge variant={getAreaVariant(project.area)}>
          {areaNames[project.area]}
        </Badge>
        <Badge variant={getStatusVariant(project.status)}>
          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
        </Badge>
        <span className="text-xs text-gray-500">
          Created: {formatDate(project.created_at)}
        </span>
      </div>

      <div className="flex justify-end space-x-2">
        <Link to={`/admin/researchs/detail/${project.id}`}>
          <Button variant="text" className="p-1">
            <Eye size={16} />
          </Button>
        </Link>
        <Link to={`/admin/researchs/edit/${project.id}`}>
          <Button variant="text" className="p-1">
            <Edit size={16} />
          </Button>
        </Link>
        <Button
          variant="text"
          className="p-1 text-red-600"
          onClick={() => handleDelete(project.id)}
        >
          <Trash2 size={16} />
        </Button>
      </div>
    </Card>
  );

  return (
    <AdminLayout>
      <PageHeader
        title="Research Management"
        subtitle="Manage and explore research projects"
        actions={
          <Button
            variant="primary"
            className="flex items-center gap-1"
            onClick={() => (window.location.href = "/admin/researchs/add")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span>Add Research</span>
          </Button>
        }
      />

      {/* Filters and Search */}
      <Card title="Search & Filter" className="mb-6" accentColor="dark">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="w-full sm:flex-1 min-w-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search research..."
                className="w-full p-2 pl-10 border rounded text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                size={16}
                className="absolute left-3 top-2.5 text-primary"
              />
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <select
              className="w-full p-2 border rounded text-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Areas</option>
              <option value="minerals">Mineral Research</option>
              <option value="cathodic">Cathodic Protection</option>
              <option value="corrosion">Corrosion Science</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Research Areas Overview */}
      <Grid cols={{ default: 1, md: 3 }} className="mb-6">
        <Card title="Mineral Research" accentColor="primary">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Active Projects</span>
            <Badge variant="success">{getActiveProjectCount("minerals")}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="text" className="text-sm flex items-center">
              <FileText size={14} className="mr-1" />
              View Reports
            </Button>
          </div>
        </Card>

        <Card title="Cathodic Protection" accentColor="primary">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Active Projects</span>
            <Badge variant="success">{getActiveProjectCount("cathodic")}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="text" className="text-sm flex items-center">
              <FileText size={14} className="mr-1" />
              View Reports
            </Button>
          </div>
        </Card>

        <Card title="Corrosion Science" accentColor="primary">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Active Projects</span>
            <Badge variant="success">
              {getActiveProjectCount("corrosion")}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="text" className="text-sm flex items-center">
              <FileText size={14} className="mr-1" />
              View Reports
            </Button>
          </div>
        </Card>
      </Grid>

      {/* Research Projects Table/Cards */}
      <Card title="Research Projects" accentColor="primary">
        {loading ? (
          <LoadingSpinner message="Memuat data penelitian..." />
        ) : error ? (
          <ErrorDisplay error={error} />
        ) : currentItems.length > 0 ? (
          <>
            {/* Desktop View - Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Research Area
                    </th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Created
                    </th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="p-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentItems.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="p-3 font-medium text-sm text-primary-dark">
                        {project.title}
                      </td>
                      <td className="p-3">
                        <Badge variant={getAreaVariant(project.area)}>
                          {areaNames[project.area]}
                        </Badge>
                      </td>
                      <td className="p-3 text-gray-700 text-sm">
                        {formatDate(project.created_at)}
                      </td>
                      <td className="p-3">
                        <Badge variant={getStatusVariant(project.status)}>
                          {project.status.charAt(0).toUpperCase() +
                            project.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center space-x-2">
                          <Link to={`/admin/researchs/detail/${project.id}`}>
                            <Button variant="text" className="p-1">
                              <Eye size={18} />
                            </Button>
                          </Link>
                          <Link to={`/admin/researchs/edit/${project.id}`}>
                            <Button variant="text" className="p-1">
                              <Edit size={18} />
                            </Button>
                          </Link>
                          <Button
                            variant="text"
                            className="p-1 text-red-600"
                            onClick={() => handleDelete(project.id)}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View - Cards */}
            <div className="md:hidden">
              {currentItems.map((project) => renderMobileCard(project))}
            </div>

            {/* Pagination Controls */}
            {filteredProjects.length > 0 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onNext={nextPage}
                onPrev={prevPage}
                onPageChange={paginate}
              />
            )}
          </>
        ) : (
          <EmptyState
            message="Tidak ada proyek penelitian ditemukan"
            action={{
              label: "Tambah Penelitian",
              onClick: () => (window.location.href = "/admin/researchs/add"),
            }}
          />
        )}
      </Card>
    </AdminLayout>
  );
};

export default Research;
